import { useState } from 'react';
import type { JobDTO } from '@devrobot/shared';
import { Fingerprint, ListChecks, ShieldCheck, ShieldX } from 'lucide-react';
import { api, seg } from '../../api/client';
import { qk, useApiMutation } from '../../api/queries';
import { useUser } from '../../api/user';
import { CopyButton } from '../../components/CopyButton';
import { ErrorAlert } from '../../components/ErrorAlert';
import { InspectionReportView } from '../../components/InspectionReport';
import { Section } from '../../components/PageHeader';
import { useToast } from '../../components/Toaster';
import { Haptic } from '../../kit/haptics';
import { haptic } from '../../kit/native';

/** Review step before the agent is installed on a new VPS (explicit adoption review). */
export function EnrollApproval({ job }: { job: JobDTO }) {
  const user = useUser();
  const toast = useToast();
  const [verified, setVerified] = useState(false);
  const report = job.approval?.report;
  const changes = job.approval?.changes ?? (Array.isArray(job.result.data?.changes) ? (job.result.data.changes as string[]) : []);
  const fingerprint = typeof job.result.data?.fingerprint === 'string' ? job.result.data.fingerprint : null;
  // Problems with a remedy (e.g. Docker missing) are fixed by the approved changes themselves.
  const blockers = report?.problems.filter((p) => p.severity === 'blocker' && !p.remedy) ?? [];

  const decide = useApiMutation((approve: boolean) => api.post<JobDTO>(`/api/v1/jobs/${seg(job.id)}/approve`, { approve }), {
    invalidate: [qk.job(job.id), ['vps'], ['jobs']],
  });

  return (
    <Section title="Review and approve enrollment" description="Nothing has been installed yet. Review what DevRobot found on the host and exactly what it will change, then approve or reject.">
      {fingerprint ? (
        <div className="flex items-start gap-3 rounded-field bg-base-200/60 p-3">
          <span className="dr-tint-secondary grid size-8 shrink-0 place-items-center rounded-full">
            <Fingerprint className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
          <p className="text-xs text-base-content/55">SSH host key fingerprint</p>
          <div className="flex items-center gap-1">
            <code className="break-anywhere font-mono text-sm font-medium">{fingerprint}</code>
            <CopyButton value={fingerprint} label="Copy fingerprint" />
          </div>
          <p className="mt-1 text-sm text-base-content/65">Compare it with the fingerprint shown in your provider’s console. If they differ, reject: you may not be talking to your server.</p>
          </div>
        </div>
      ) : null}

      <div>
        <p className="mb-2 text-sm font-semibold">DevRobot will make these changes</p>
        {changes.length > 0 ? (
          <ul className="divide-y divide-hairline rounded-field border border-hairline text-sm">
            {changes.map((c, i) => (
              <li key={i} className="flex items-start gap-2.5 px-3 py-2">
                <ListChecks className="mt-0.5 size-4 shrink-0 text-base-content/45" aria-hidden="true" />
                <span className="break-anywhere">{c}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-base-content/60">The change list was not reported.</p>
        )}
      </div>

      {report ? (
        <details className="collapse-arrow collapse rounded-field border border-hairline" open>
          <summary className="collapse-title text-sm font-semibold">Inspection report</summary>
          <div className="collapse-content">
            <InspectionReportView report={report} />
          </div>
        </details>
      ) : (
        <p className="text-sm text-base-content/60">No inspection report was attached to this job.</p>
      )}

      {blockers.length > 0 ? (
        <div role="alert" className="alert alert-error alert-soft text-sm">
          {blockers.length} blocking problem{blockers.length === 1 ? '' : 's'} found. Installation will most likely fail until {blockers.length === 1 ? 'it is' : 'they are'} fixed on the host.
        </div>
      ) : null}

      {user.platformAdmin ? (
        <>
          <label className="flex cursor-pointer items-start gap-3 rounded-field bg-base-200/60 p-3 text-sm">
            <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
            <span>I verified the host identity and reviewed the changes above.</span>
          </label>
          <ErrorAlert error={decide.error} />
          <div className="flex flex-wrap justify-end gap-2">
            <Haptic disabled={decide.isPending}>
              <button
                type="button"
                className="btn btn-outline btn-error"
                disabled={decide.isPending}
                onClick={() => decide.mutate(false, { onSuccess: () => toast.info('Enrollment rejected', { description: 'Nothing was installed.' }), onError: () => haptic('error') })}
              >
                <ShieldX className="size-4" aria-hidden="true" />
                Reject
              </button>
            </Haptic>
            <Haptic disabled={decide.isPending || !verified}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={decide.isPending || !verified}
                onClick={() => decide.mutate(true, { onSuccess: () => toast.success('Approved', { description: 'Installation is running.' }), onError: () => haptic('error') })}
              >
                {decide.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : <ShieldCheck className="size-4" aria-hidden="true" />}
                Approve and install
              </button>
            </Haptic>
          </div>
        </>
      ) : (
        <div role="note" className="alert alert-info alert-soft text-sm">
          Only a platform administrator can approve or reject enrollment.
        </div>
      )}
    </Section>
  );
}
